import type { Animation, Timeline } from "../schema/timeline";

export interface StepItem {
  anim: Animation;
  /** ms from step start */
  startMs: number;
  /** ms from step start; Infinity for untilNextClick / untilEndOfSlide */
  endMs: number;
}

export interface Step {
  index: number;
  /** true when the step runs automatically on slide enter (no click) */
  auto: boolean;
  items: StepItem[];
  /** max finite end in ms */
  totalMs: number;
}

export function iterationsOf(anim: Animation): number {
  if (typeof anim.repeat === "number") return Math.max(1, anim.repeat);
  return Infinity;
}

export function activeDuration(anim: Animation): number {
  const iters = iterationsOf(anim);
  if (!Number.isFinite(iters)) return Infinity;
  const per = anim.duration * (anim.autoReverse ? 2 : 1);
  return per * iters;
}

/**
 * Compile the main sequence into click steps. A new step begins at every `onClick`
 * animation. Leading with/after animations form an automatic step 0.
 */
export function compileSteps(timeline: Timeline): Step[] {
  const steps: Step[] = [];
  let current: Step | undefined;
  let prevStart = 0;
  let maxEnd = 0;

  const open = (auto: boolean): Step => {
    const step: Step = { index: steps.length, auto, items: [], totalMs: 0 };
    steps.push(step);
    prevStart = 0;
    maxEnd = 0;
    return step;
  };

  for (const anim of timeline.main) {
    if (anim.trigger === "onClick" || current === undefined) {
      current = open(current === undefined && anim.trigger !== "onClick");
    }
    const step = current;
    let start: number;
    if (anim.trigger === "afterPrevious") {
      start = (Number.isFinite(maxEnd) ? maxEnd : prevStart) + anim.delay;
    } else if (anim.trigger === "withPrevious") {
      start = prevStart + anim.delay;
    } else {
      start = anim.delay;
    }
    const dur = activeDuration(anim);
    const end = Number.isFinite(dur) ? start + dur : Infinity;
    step.items.push({ anim, startMs: start, endMs: end });
    prevStart = start;
    if (Number.isFinite(end)) {
      maxEnd = Math.max(maxEnd, end);
      step.totalMs = Math.max(step.totalMs, end);
    }
  }
  return steps;
}

/** Number of click positions on a page: -1 = before any auto step, then 0..steps.length-1. */
export function lastStepIndex(steps: Step[]): number {
  return steps.length - 1;
}

/** The step index a page rests at when entered: -1 when there is no auto step, else 0 (auto step plays). */
export function entryStep(steps: Step[]): number {
  return steps.length > 0 && steps[0]!.auto ? 0 : -1;
}
